// src/components/DoradztwoStrategiczne.js

import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Target, Lightbulb, BarChart3, Handshake, Rocket, CheckCircle, ArrowRight, ChevronRight } from 'lucide-react';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const DoradztwoStrategiczne = () => {
  const services = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Strategie rozwoju",
      description: "Opracowujemy kompleksowe strategie rozwoju dostosowane do specyfiki Twojej branży i celów biznesowych."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Analizy rynkowe",
      description: "Przeprowadzamy szczegółowe analizy rynku, konkurencji i trendów branżowych wspierające decyzje biznesowe."
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: "Fuzje i przejęcia",
      description: "Doradzamy przy transakcjach M&A, od wyceny przez due diligence po finalizację transakcji."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innowacje biznesowe",
      description: "Wspieramy wdrażanie innowacji, transformację cyfrową i optymalizację procesów biznesowych."
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Ekspansja rynkowa",
      description: "Pomagamy w planowaniu i realizacji ekspansji na nowe rynki krajowe i zagraniczne."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Restrukturyzacja",
      description: "Przeprowadzamy procesy restrukturyzacji operacyjnej i finansowej zwiększające efektywność firmy."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Diagnoza",
      description: "Analizujemy obecną sytuację firmy, jej mocne i słabe strony oraz potencjał rozwojowy."
    },
    {
      step: "02",
      title: "Strategia",
      description: "Opracowujemy szczegółową strategię działania z konkretnymi celami i miernikami sukcesu."
    },
    {
      step: "03",
      title: "Wdrożenie",
      description: "Wspieramy implementację strategii, monitorując postępy i dostosowując działania."
    },
    {
      step: "04",
      title: "Optymalizacja",
      description: "Na bieżąco optymalizujemy procesy i działania, maksymalizując efekty biznesowe."
    }
  ];

  const stats = [
    { value: "200+", label: "Zrealizowanych projektów" },
    { value: "50M+", label: "PLN wzrostu przychodów klientów" },
    { value: "15+", label: "Lat doświadczenia" },
    { value: "95%", label: "Zadowolonych klientów" }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <TrendingUp className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Target className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Doradztwo strategiczne i rozwój biznesu
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
              Pomagamy firmom osiągać ambitne cele biznesowe. Od analizy strategicznej, przez optymalizację procesów, po wsparcie w ekspansji - jesteśmy partnerem w rozwoju Twojego przedsiębiorstwa.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleContactScroll}
              className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-violet-600/25 hover:scale-105 transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                Rozpocznij transformację
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-violet-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Obszary naszego wsparcia
            </h2>
            <p className="text-lg text-gray-400">
              Kompleksowe doradztwo dostosowane do Twoich potrzeb biznesowych
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Jak działamy?
            </h2>
            <p className="text-lg text-gray-400">
              Sprawdzony proces prowadzący do sukcesu Twojego biznesu
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-black text-violet-600/20 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-400">
                  {item.description}
                </p>
                {index < process.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute top-12 -right-4 w-8 h-8 text-violet-600/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="backdrop-blur-md bg-white/5 rounded-2xl p-12 border border-white/10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Gotowy na przełom w biznesie?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Skontaktuj się z nami i odkryj pełen potencjał swojej firmy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleContactScroll}
                className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-violet-600/25 hover:scale-105 transition-all duration-300"
              >
                <span className="flex items-center justify-center gap-2">
                  Umów bezpłatną konsultację
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <a 
                href="tel:+48791335232"
                className="px-8 py-4 bg-black/20 backdrop-blur-sm border border-white/20 rounded-lg text-white font-semibold hover:bg-white/10 transition-all duration-300 text-center"
              >
                Zadzwoń: 791 335 232
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default DoradztwoStrategiczne;